// src/js-dot/ui/functions/$append.ts
function $append(parent, ...children) {
  if (children.length === 0) {
    children[0] = parent;
    parent = globalThis["document"]?.body;
    if (!parent) {
      throw ReferenceError("Cannot Found Document");
    }
  }
  const end = children.length;
  let current = -1;
  let child;
  while (++current < end) {
    child = children[current];
    parent.appendChild(child);
    parent = child;
  }
  return child;
}

// src/js-dot/ui/functions/$remove.ts
function $remove(element) {
  element.parentNode.removeChild(element);
}

// src/js-dot/ui/functions/$tag.ts
function $tag(document_or_tagName_or_properties, tagName_or_properties, properties) {
  let document_ = document_or_tagName_or_properties;
  let tagName = tagName_or_properties;
  if (typeof document_ === "string") {
    properties = tagName;
    tagName = document_;
    document_ = document;
  } else if (typeof document_["createElement"] !== "function") {
    properties = document_;
    tagName = "div";
    document_ = document;
  }
  if (typeof tagName !== "string") {
    properties = tagName;
    tagName = "div";
  } else if (typeof properties === "string") {
    switch (properties.charAt(0)) {
      case "#":
        properties = {
          id: properties.slice(1)
        };
        break;
      case ".":
        properties = properties.slice(1);
      default:
        properties = {
          className: properties.split(".").join(" ")
        };
    }
  }
  const node = document_.createElement(tagName || "div");
  if (!properties) {
    return node;
  }
  const propertyKeys = Object.keys(properties);
  let propertyCurrent = propertyKeys.length;
  while (propertyCurrent-- > 0) {
    const key = propertyKeys[propertyCurrent];
    const value = properties[key];
    if (typeof value === "object") {
      const nodeTarget = node[key];
      if (typeof nodeTarget === "object") {
        const valueKeys = Object.keys(value);
        let valueCurrent = valueKeys.length;
        while (valueCurrent-- > 0) {
          const valueKey = valueKeys[valueCurrent];
          nodeTarget[valueKey] = value[valueKey];
        }
        continue;
      }
    }
    node[key] = value;
  }
  return node;
}

// src/js-dot/ui/functions/$visible.ts
function $visible(element) {
  return new Promise(resolve => {
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry.isIntersecting) {
        observer.disconnect();
        resolve(entry.target);
      }
    });
    observer.observe(element);
  });
}

// src/js-dot/ui/functions/by-id.ts
function byId(parent_or_id, id) {
  let parent = parent_or_id;
  if (!id) {
    id = parent;
    parent = null;
  }
  return (parent || document).getElementById(id);
}

// src/js-dot/ui/classes/class-switch.ts
import { toPrefixWith } from "@js-dot/core";
var ClassSwitch = class _ClassSwitch {
  /**
   * Get Singleton Instance from Element
   */
  static of(element, option) {
    return element["_dotClassSwitch"] || (element["_dotClassSwitch"] = new _ClassSwitch(element, option));
  }
  _classList;
  _lastClassName;
  _prefix;
  _host;
  constructor(element, option) {
    this._host = element;
    this._classList = element.classList;
    this._prefix = option?.prefix || "";
  }
  _set(...classNames) {
    const {
      _classList,
      _lastClassName
    } = this;
    if (_lastClassName) {
      _classList.remove(..._lastClassName);
    }
    const {
      _prefix
    } = this;
    if (_prefix) {
      classNames = toPrefixWith(classNames, _prefix);
    }
    let current = classNames.length;
    while (current-- > 0) {
      classNames[current] = classNames[current].replace(/\//g, "-");
    }
    _classList.add(...classNames);
    this._lastClassName = classNames;
    return this;
  }
  /**
   * Set New Class Names
   * @param classNames
   */
  set(...classNames) {
    return this._set(...classNames);
  }
  /**
   * Unset Class Names
   */
  pop() {
    const {
      _lastClassName
    } = this;
    if (_lastClassName) {
      this._classList.remove(..._lastClassName);
      this._lastClassName = null;
    }
    return this;
  }
};

// src/js-dot/ui/classes/class-toggle.ts
var ClassToggle = class {
  _classList;
  _whenTrue;
  _whenFalse;
  _lastFlag;
  constructor(node, whenTrue, whenFalse) {
    this._classList = node.classList;
    this._whenTrue = whenTrue;
    this._whenFalse = whenFalse;
  }
  /**
   * Set Enabled Class Name and Disabled Class Name as Optional
   * @param whenTrue
   * @param whenFalse
   */
  setClassName(whenTrue, whenFalse) {
    this._whenTrue = whenTrue;
    this._whenFalse = whenFalse;
    return this;
  }
  /**
   * Set Enabled Class Name
   * @param whenTrue
   */
  setTrueClassName(whenTrue) {
    this._whenTrue = whenTrue;
    return this;
  }
  /**
   * Disabled Class Name
   * @param whenFalse
   */
  setFalseClassName(whenFalse) {
    this._whenFalse = whenFalse;
    return this;
  }
  /**
   * Set Class Toggle State
   * @param flag
   */
  set(flag = true) {
    const {
      _classList,
      _whenTrue,
      _whenFalse
    } = this;
    if (flag === this._lastFlag) {
      return this;
    }
    this._lastFlag = flag;
    if (flag) {
      _whenFalse && _classList.remove(_whenFalse);
      _classList.add(_whenTrue);
    } else {
      _whenFalse && _classList.add(_whenFalse);
      _classList.remove(_whenTrue);
    }
    return this;
  }
  /**
   * Remove Class Names
   */
  pop() {
    if (this._lastFlag === void 0) {
      return this;
    }
    this._classList.remove(this._whenTrue, this._whenFalse);
    return this;
  }
  /**
   * Apply Truthy Class Name
   * @param flag
   */
  enable(flag = true) {
    this.set(flag);
  }
  /**
   * Apply Falsy Class Name
   * @param flag
   */
  disable(flag = true) {
    return this.enable(!flag);
  }
};

// src/js-dot/ui/classes/key-map.ts
var SHORTEN_KEYS = {
  "esc": "escape"
};
var KeyMap = class {
  _container;
  _selfKeyUp = this._keyUp.bind(this);
  _callbacks = {};
  _count = 0;
  constructor(container) {
    if (!container) {
      container = document.body;
    }
    this._container = container;
  }
  _keyUp(event) {
    const target = event.target;
    switch (target.tagName) {
      case "INPUT":
      case "TEXTAREA":
        if (target.value) {
          return;
        }
      case "SELECT":
        if (target.selectedIndex) {
          return;
        }
    }
    const key = event.key.toLowerCase();
    const {
      _callbacks
    } = this;
    const callbacks = _callbacks[key];
    if (callbacks && callbacks.length) {
      const callback = callbacks.pop();
      callback.callback();
      this._decrease();
    }
  }
  _increase() {
    if (this._count === 0) {
      this._container.addEventListener("keyup", this._selfKeyUp);
    }
    this._count++;
  }
  _decrease() {
    this._count--;
    if (this._count === 0) {
      this._container.removeEventListener("keyup", this._selfKeyUp);
    }
  }
  add(key, callback) {
    key = SHORTEN_KEYS[key] || key;
    const {
      _callbacks
    } = this;
    const listener = new KeyMapListener(this, key, callback);
    if (!_callbacks[key]) {
      _callbacks[key] = [listener];
    } else {
      const callbacks = _callbacks[key];
      callbacks[callbacks.length] = listener;
    }
    this._increase();
    return listener;
  }
  remove(listener) {
    const {
      key
    } = listener;
    const {
      _callbacks
    } = this;
    const callbacks = _callbacks[key];
    if (!callbacks) {
      return this;
    }
    const index = callbacks.indexOf(listener);
    if (index > -1) {
      callbacks.splice(index, 1);
      this._decrease();
    }
    return this;
  }
};
var KeyMapListener = class {
  constructor(_keyMap, key, callback) {
    this._keyMap = _keyMap;
    this.key = key;
    this.callback = callback;
  }
  remove() {
    this._keyMap.remove(this);
  }
};

// src/js-dot/ui/renders/render-date.ts
function renderDate(value) {
  const date = value instanceof Date ? value : new Date(value);
  if (isNaN(date.getTime())) {
    return "";
  }
  const y = "" + date.getFullYear();
  const m = zerofill(date.getMonth() + 1);
  const d = zerofill(date.getDate());
  const h = zerofill(date.getHours());
  const i = zerofill(date.getMinutes());
  const s = zerofill(date.getSeconds());
  const datetime = `${y}-${m}-${d} ${h}:${i}:${s}`;
  const title = date.toLocaleString();
  return [`<time class="timestamp" datetime="${datetime}" title="${title}">`, `<span class="y">`, `<var class="y-prefix">${y.substr(0, 2)}</var>`, `<var class="y-suffix">${y.slice(2)}</var>`, `</span>`, `<span class="m"><span>-</span><var>${m}</var></span>`, `<span class="d"><span>-</span><var>${d}</var></span>`, `<span class="h"><span>&nbsp;</span><var>${h}</var></span>`, `<span class="i"><span>:</span><var>${i}</var></span>`, `<span class="s"><span>:</span><var>${s}</var></span>`, `</time>`].join("");
}
function zerofill(value) {
  return value < 10 ? "0" + value : value;
}

// src/js-dot/ui/renders/render-email.ts
function renderEmail(value) {
  if (!value) {
    return value;
  }
  const [account, host] = ("" + value).split("@");
  return ['<span class="emails">', `<span class="email-account">${account}</span>`, '<span class="email-at-host">', `<span class="email-at">@</span>`, `<span class="email-host">${host}</span>`, "</span>", "</span>"].join("");
}

// src/js-dot/ui/renders/render-icon.ts
import { isNone } from "@js-dot/core";
var _URL = /^((https?:)?\/\/)|\.{0,2}\//;
var _IMG = /\.(gif|png|jpe?g|svg)/;
function renderIcon(type, label) {
  if (!type) {
    return isNone(label) ? "" : "" + label;
  }
  if (_IMG.test(type) || _URL.test(type)) {
    return `
			<span class="icons">
				${label ? `
					<img src="${type}" class="icon-image" alt="${label}">
					${renderIconLabel(label)}
				` : `
					<img src="${type}" class="icon-image">
				`}
			</span>
		`;
  }
  const [prefix, suffix] = formalIconType(type);
  return `
		<span class="icons">
			  <i class="${prefix} ${prefix}-${suffix}"></i>
			  ${label ? renderIconLabel(label) : ""}
		</span>
		`;
}
function formalIconType(type) {
  const prefixIndex = type.indexOf(":");
  if (prefixIndex > -1 && type.charAt(prefixIndex + 1) !== "/") {
    return type.split(":");
  }
  return ["icon", type];
}
function renderIconLabel(label) {
  return `<span class="icon-label">${label}</span>`;
}

// src/js-dot/ui/renders/render-ip.ts
import { toWrapString } from "@js-dot/core";
function renderIp(ip) {
  return ip ? ip.indexOf(":") > -1 ? renderIp6(ip) : renderIp4(ip) : ip;
}
function renderIp4(ip) {
  const segments = ip.split(".");
  return toWrapString(segments, {
    firstStarts: '<span class="ip ip4">',
    starts: `<var>`,
    ends: `</var>`,
    separator: "<span>.</span>",
    lastEnds: "</span>"
  });
}
function renderIp6(ip) {
  const segments = ip.split(":");
  let current = segments.length;
  while (current-- > 0) {
    segments[current] = segments[current].replace(/^0+/, "");
  }
  return toWrapString(segments, {
    firstStarts: '<span class="ip ip6">',
    starts: "<var>",
    ends: "</var>",
    separator: "<span>:</span>",
    lastEnds: "</span>"
  });
}

// src/js-dot/ui/renders/render-non-kr.ts
function renderNonKr(value) {
  const _NON_KR = /[^가-힣]|\s/;
  const _TAG_START = /[a-z\/]/;
  return (renderNonKr = function (value2) {
    if (value2 === void 0 || value2 === null) {
      return value2;
    }
    value2 = "" + value2;
    const end = value2.length;
    let current = -1;
    let result = "";
    let nonKr = "";
    function addNoneKr() {
      if (nonKr) {
        result += '<span class="non-kr">' + nonKr + "</span>";
        nonKr = "";
      }
    }
    root: while (++current < end) {
      let char = value2.charAt(current);
      if (char === "<") {
        if (_TAG_START.test(value2.charAt(current + 1))) {
          addNoneKr();
          result += char;
          while (++current < end) {
            char = value2.charAt(current);
            result += char;
            if (char === ">") {
              continue root;
            }
          }
        }
      }
      if (_NON_KR.test(char)) {
        nonKr += char;
      } else {
        addNoneKr();
        result += char;
      }
    }
    addNoneKr();
    return result;
  }).apply(this, arguments);
}

// src/js-dot/ui/renders/render-paragraph.ts
function renderParagraph(text) {
  const P_SEPARATOR = /[\n\r][\t\s]*[\n\r][\n\r\t\s]*/g;
  const BR_SEPARATOR = /[\n\r][\t\s]*/g;
  const SPACE = /(\t+)/g;
  return (renderParagraph = function (text2) {
    if (!text2 || !text2.length) {
      return "";
    }
    text2 = text2.replace(SPACE, (substring, ...args) => {
      return "&nbsp;".repeat(substring.length * 4);
    });
    const pArray = text2.trim().split(P_SEPARATOR);
    const end = pArray.length;
    let current = -1;
    while (++current < end) {
      pArray[current] = pArray[current].replace(BR_SEPARATOR, "<br>");
    }
    return "<p>" + pArray.join("</p><p>") + "</p>";
  }).apply(this, arguments);
}

// src/js-dot/ui/renders/render-tel.ts
function renderTel(value) {
  return `<span class="tel">${value}</span>`;
}

// src/js-dot/ui/renders/get-renderer.ts
import { renderDate as renderDate2, renderEmail as renderEmail2, renderHost, renderIp as renderIp2 } from "@js-dot/ui";
function getRenderer(expressionType) {
  switch (expressionType) {
    case "ip":
      return renderIp2;
    case "host":
      return renderHost;
    case "email":
      return renderEmail2;
    case "timestamp":
      return renderDate2;
    case "numeric":
      return renderNumeric;
  }
}
function renderNumeric(value) {
  return value ? value.toLocaleString() : value;
}

// src/js-dot/ui/renders/render-table.ts
import { toWrapString as toWrapString2 } from "@js-dot/core";
function renderTable(items, option) {
  if (!option) {
    option = {};
  }
  const header = toWrapString2(option?.headers || items.shift(), "<th>", "</th>");
  const end = items.length;
  let current = -1;
  let body = [];
  while (++current < end) {
    body[body.length] = toWrapString2(items[current], "<td>", "</td>");
  }
  return `<table><thead><tr>${header}</tr></thead><tbody>${toWrapString2(body, "<tr>", "</tr>")}</tbody></table>`;
}

// src/js-dot/ui/renders/render-host.ts
import { getHostType, parseUrl } from "@js-dot/core";
function renderHost2(value) {
  let {
    host,
    port
  } = parseUrl(value);
  switch (getHostType(host)) {
    case "ip4":
      host = renderIp4(host);
      break;
    case "ip6":
      host = renderIp6(host);
      break;
  }
  return `<span class="host">
${host ? `<span class="domain">${host}</span>` : ""}
${port ? `<span class="port"><span>:</span><var>${port}</var></span>` : ""}
	</span>`;
}

// src/js-dot/ui/renders/render-url-to-link.ts
import { _URLS } from "@js-dot/core";
function renderUrlToLink(content) {
  return content ? content.replace(_URLS, '<a href="$1" target="_blank">$1</a>') : content;
}
export { $append, $remove, $tag, $visible, ClassSwitch, ClassToggle, KeyMap, KeyMapListener, byId, getRenderer, renderDate, renderEmail, renderHost2 as renderHost, renderIcon, renderIp, renderIp4, renderIp6, renderNonKr, renderParagraph, renderTable, renderTel, renderUrlToLink };
